import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ArrowLeft, ArrowRight, Loader2, Camera, FileDown, ClipboardCheck, Trash2, Video, PenLine, Monitor, Stethoscope, Syringe, Upload, History } from "lucide-react";
import StepMansioni from "./dvr/steps/StepMansioni";
import StepPericoli from "./dvr/steps/StepPericoli";
import DVRAuditPanel from "./dvr/DVRAuditPanel";
import StepAttrezzature from "./dvr/steps/StepAttrezzature";
import StepSostanze from "./dvr/steps/StepSostanze";
import StepLavoratriciMadri from "./dvr/steps/StepLavoratriciMadri";
import StepMinorenni from "./dvr/steps/StepMinorenni";
import StepApprendisti from "./dvr/steps/StepApprendisti";
import StepStranieri from "./dvr/steps/StepStranieri";
import StepLavoriEstero from "./dvr/steps/StepLavoriEstero";
import StepSegnaletica from "./dvr/steps/StepSegnaletica";
import StepPianoMiglioramento from "./dvr/steps/StepPianoMiglioramento";
import StepNioshMultitask from "./dvr/steps/StepNioshMultitask";
import StepOcraMultitask from "./dvr/steps/StepOcraMultitask";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STEP_KEYS = [
  { key: "step1_dati_generali", label: "1. Dati generali" },
  { key: "step2_classificazione", label: "2. Classificazione aziendale" },
  { key: "step3_territoriale", label: "3. Rischio territoriale" },
  { key: "step4_mansioni", label: "4. Mansioni" },
  { key: "step5_pericoli", label: "5. Pericoli" },
  { key: "attrezzature", label: "6. Attrezzature" },
  { key: "sostanze", label: "7. Sostanze chimiche" },
  { key: "vdt", label: "8. VDT (Videoterminali)" },
  { key: "incendio", label: "9. Incendio (DM 3/9/2021)" },
  { key: "sorveglianza_sanitaria", label: "10. Sorveglianza sanitaria" },
  { key: "niosh", label: "11. NIOSH video" },
  { key: "ocra", label: "12. OCRA video" },
  { key: "lavoratrici_madri", label: "13. Lavoratrici madri" },
  { key: "minorenni", label: "14. Minorenni" },
  { key: "apprendisti", label: "15. Apprendisti" },
  { key: "stranieri", label: "16. Lavoratori stranieri" },
  { key: "lavori_estero", label: "17. Lavori all'estero" },
  { key: "segnaletica", label: "18. Segnaletica" },
  { key: "piano_miglioramento", label: "19. Piano miglioramento" },
  { key: "firme", label: "20. Firme" },
  { key: "genera", label: "21. Genera DVR" },
];

export default function DVREditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dvr, setDvr] = useState(null);
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const saveTimer = useRef(null);

  const load = useCallback(() => api.get(`/dvr/documents/${id}`).then((r) => setDvr(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-save with debounce
  const autoSave = (stepKey, data) => {
    setDvr((prev) => ({ ...prev, steps: { ...prev.steps, [stepKey]: data } }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.patch(`/dvr/documents/${id}/step/${stepKey}`, { data });
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : (err.message || "Salvataggio fallito"));
        toast.error(`Salvataggio fallito: ${msg}`);
      }
      setSaving(false);
    }, 900);
  };

  const runAI = async (endpoint, payload, stepKey) => {
    setAiLoading(true);
    try {
      const r = await api.post(endpoint, payload);
      autoSave(stepKey, r.data);
      toast.success("Analisi AI completata");
    } catch (err) { toast.error(err.response?.data?.detail || "AI error"); }
    setAiLoading(false);
  };

  const generateDocx = async () => {
    setGenLoading(true);
    try {
      const r = await api.post(`/dvr/documents/${id}/generate`);
      toast.success(`DVR generato (v${r.data.version})`);
      window.open(`${BACKEND}${r.data.docx_url}`, "_blank");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore generazione"); }
    setGenLoading(false);
  };

  if (!dvr) return <div className="text-muted-foreground">Caricamento…</div>;
  const step = STEP_KEYS[current];
  const LIST_STEPS = ["step4_mansioni", "step5_pericoli", "attrezzature", "sostanze", "vdt", "niosh", "ocra", "sorveglianza_sanitaria"];
  const stepData = dvr.steps?.[step.key] ?? (LIST_STEPS.includes(step.key) ? [] : {});

  return (
    <div data-testid="dvr-editor" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/dvr" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> DVR
        </Link>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAuditOpen(true)}
            className="rounded-full"
            data-testid="dvr-open-audit"
            title="Cronologia modifiche (audit trail)"
          >
            <History className="w-4 h-4 mr-1.5" /> Cronologia
          </Button>
          <div className="text-xs text-muted-foreground">
            {saving ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Salvataggio…</span> : "✓ Salvato"}
          </div>
        </div>
      </div>
      {auditOpen && <DVRAuditPanel dvrId={id} onClose={() => setAuditOpen(false)} />}

      <div>
        <h1 className="text-2xl font-bold tracking-tight">{dvr.title}</h1>
        <p className="text-sm text-muted-foreground">{dvr.customer_name} · Progresso {dvr.progress_pct || 0}%</p>
        <div className="w-full bg-muted rounded-full h-1.5 mt-3 overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${dvr.progress_pct || 0}%` }} />
        </div>
      </div>

      {/* Step navigator */}
      <div className="flex flex-wrap gap-1.5">
        {STEP_KEYS.map((s, i) => (
          <button
            key={s.key}
            onClick={() => setCurrent(i)}
            data-testid={`dvr-step-${i}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${i === current ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Step content */}
      <div className="bg-card border border-border rounded-lg p-6 min-h-[400px]">
        <h2 className="text-lg font-semibold mb-4">{step.label}</h2>
        {step.key === "step1_dati_generali" && <Step1 data={stepData} onChange={(d) => autoSave(step.key, d)} />}
        {step.key === "step2_classificazione" && <Step2 dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} runAI={runAI} aiLoading={aiLoading} />}
        {step.key === "step3_territoriale" && <Step3 dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} runAI={runAI} aiLoading={aiLoading} />}
        {step.key === "step4_mansioni" && <StepMansioni dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "step5_pericoli" && <StepPericoli dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "attrezzature" && <StepAttrezzature data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "sostanze" && <StepSostanze data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "vdt" && <StepVDT data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "incendio" && <StepIncendio dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "sorveglianza_sanitaria" && <StepSorveglianza dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "niosh" && <StepNioshTabs dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "ocra" && <StepOcraTabs dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "lavoratrici_madri" && <StepLavoratriciMadri dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "minorenni" && <StepMinorenni dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "apprendisti" && <StepApprendisti dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "stranieri" && <StepStranieri dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "lavori_estero" && <StepLavoriEstero dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "segnaletica" && <StepSegnaletica dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "piano_miglioramento" && <StepPianoMiglioramento dvr={dvr} data={stepData} onChange={(d) => autoSave(step.key, d)} aiLoading={aiLoading} setAiLoading={setAiLoading} />}
        {step.key === "firme" && <StepFirme dvr={dvr} onSigned={load} />}
        {step.key === "genera" && (
          <div className="text-center space-y-4 py-8">
            <ClipboardCheck className="w-12 h-12 mx-auto text-primary" />
            <p className="text-muted-foreground">Genera il documento finale in formato Word (.docx). Potrai modificarlo liberamente prima della firma.</p>
            <Button onClick={generateDocx} disabled={genLoading} data-testid="dvr-generate-btn" className="rounded-full btn-lift">
              {genLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Genera DVR (.docx)
            </Button>
            {dvr.revisions?.length > 0 && (
              <div className="text-left max-w-md mx-auto">
                <h3 className="text-sm font-semibold mb-2">Revisioni</h3>
                <ul className="text-xs space-y-1">
                  {dvr.revisions.map((r) => (
                    <li key={r.id} className="flex items-center justify-between">
                      <span>v{r.version} · {r.created_at?.slice(0, 16).replace("T", " ")}</span>
                      <a href={`${BACKEND}${r.docx_url}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">Scarica</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" disabled={current === 0} onClick={() => setCurrent((c) => c - 1)} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Indietro
        </Button>
        <Button disabled={current === STEP_KEYS.length - 1} onClick={() => setCurrent((c) => c + 1)} className="rounded-full">
          Avanti <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

/* -------- Step components -------- */
function Step1({ data, onChange }) {
  const d = data || {};
  const update = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-4">
      <div><Label>Ragione sociale</Label><Input value={d.ragione_sociale || ""} onChange={(e) => update("ragione_sociale", e.target.value)} data-testid="s1-name" /></div>
      <div><Label>Attività (tipo)</Label><Input value={d.tipo_attivita || ""} onChange={(e) => update("tipo_attivita", e.target.value)} data-testid="s1-attivita" /></div>
      <div><Label>Indirizzo sede</Label><Input value={d.indirizzo_sede || ""} onChange={(e) => update("indirizzo_sede", e.target.value)} data-testid="s1-sede" /></div>
      <div><Label>Indirizzo legale</Label><Input value={d.indirizzo_legale || ""} onChange={(e) => update("indirizzo_legale", e.target.value)} /></div>
      <div><Label>ATECO</Label><Input value={d.ateco || ""} onChange={(e) => update("ateco", e.target.value)} /></div>
      <div><Label>N° lavoratori</Label><Input type="number" value={d.numero_lavoratori || ""} onChange={(e) => update("numero_lavoratori", Number(e.target.value))} /></div>
      <div><Label>N° donne</Label><Input type="number" value={d.numero_donne || ""} onChange={(e) => update("numero_donne", Number(e.target.value))} /></div>
      <div><Label>N° minorenni</Label><Input type="number" value={d.numero_minorenni || ""} onChange={(e) => update("numero_minorenni", Number(e.target.value))} /></div>
      <div><Label>N° apprendisti</Label><Input type="number" value={d.numero_apprendisti || ""} onChange={(e) => update("numero_apprendisti", Number(e.target.value))} /></div>
      <div><Label>N° stranieri</Label><Input type="number" value={d.numero_stranieri || ""} onChange={(e) => update("numero_stranieri", Number(e.target.value))} /></div>
      <div className="col-span-2 border-t border-border pt-4 mt-2">
        <h3 className="font-semibold text-sm mb-3">Organigramma</h3>
      </div>
      {["datore_di_lavoro", "rspp", "rls", "rlst", "medico_competente", "preposti", "dirigenti", "addetti_antincendio", "addetti_primo_soccorso"].map((k) => (
        <div key={k}><Label>{k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</Label><Input value={d[k] || ""} onChange={(e) => update(k, e.target.value)} /></div>
      ))}
    </div>
  );
}

function Step2({ dvr, data, onChange, runAI, aiLoading }) {
  const s1 = dvr.steps?.step1_dati_generali || {};
  const entries = Object.entries(data || {});
  const run = () => runAI("/dvr/ai/classify", {
    ateco: s1.ateco, tipo_attivita: s1.tipo_attivita, numero_lavoratori: s1.numero_lavoratori,
    numero_donne: s1.numero_donne, superficie_mq: s1.superficie_mq,
  }, "step2_classificazione");

  const updateField = (key, patch) => {
    const cur = data?.[key] || {};
    onChange({ ...(data || {}), [key]: { ...cur, ...patch } });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        L'AI classificherà: classe primo soccorso, livello antincendio, livello formazione,
        obbligo piano emergenza e CPI (DPR 151/2011). <b>Tutti i valori sono modificabili
        manualmente</b> — le modifiche verranno riportate nel DVR come "override manuale".
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={run} disabled={aiLoading} data-testid="s2-run-ai" className="rounded-full">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {entries.length > 0 ? "Rianalizza con AI" : "Analizza con AI"}
        </Button>
        {entries.length > 0 && (
          <span className="text-xs text-muted-foreground italic">
            Modifica i campi qui sotto per sovrascrivere l'output AI.
          </span>
        )}
      </div>
      {entries.length > 0 && (
        <div className="grid gap-3 mt-4">
          {entries.map(([k, v]) => (
            <Step2Field
              key={k}
              fieldKey={k}
              value={v}
              onChange={(patch) => updateField(k, patch)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Step2Field({ fieldKey, value, onChange }) {
  const v = value || {};
  const isManual = !!v.manual_override;
  return (
    <div className={`border rounded-md p-3 ${isManual ? "border-amber-300 bg-amber-50/50" : "border-border bg-muted/30"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {fieldKey.replace(/_/g, " ")}
        </div>
        {isManual && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-semibold" title={`Override manuale (era: ${v.ai_valore ?? "—"})`}>
            OVERRIDE
          </span>
        )}
      </div>
      <div className="mt-2">
        <Label className="text-[11px]">Valore</Label>
        <Input
          value={v.valore ?? ""}
          onChange={(e) => onChange({
            valore: e.target.value,
            manual_override: e.target.value !== v.ai_valore,
            // Preserve original AI value for audit trail
            ai_valore: v.ai_valore ?? v.valore,
          })}
          data-testid={`s2-val-${fieldKey}`}
        />
      </div>
      <div className="mt-2">
        <Label className="text-[11px]">Motivazione / Riferimento normativo</Label>
        <Textarea
          rows={2}
          value={v.motivazione ?? ""}
          onChange={(e) => onChange({
            motivazione: e.target.value,
            ai_motivazione: v.ai_motivazione ?? v.motivazione,
          })}
          data-testid={`s2-mot-${fieldKey}`}
        />
      </div>
      {isManual && (
        <button
          type="button"
          onClick={() => onChange({
            valore: v.ai_valore ?? "",
            motivazione: v.ai_motivazione ?? "",
            manual_override: false,
          })}
          className="mt-2 text-[11px] text-primary hover:underline"
          data-testid={`s2-reset-${fieldKey}`}
        >
          ↺ Ripristina valore AI
        </button>
      )}
    </div>
  );
}

function Step3({ dvr, data, onChange, runAI, aiLoading }) {
  const s1 = dvr.steps?.step1_dati_generali || {};
  const [descr, setDescr] = useState(data?._input_descr || "");
  const run = () => runAI("/dvr/ai/territorial-risk", {
    indirizzo_sede: s1.indirizzo_sede, piano: s1.piano, descrizione_edificio: descr,
  }, "step3_territoriale");
  return (
    <div className="space-y-4">
      <Textarea rows={2} value={descr} onChange={(e) => setDescr(e.target.value)} placeholder="Descrizione edificio (piano, tipologia, età)" data-testid="s3-descr" />
      <Button onClick={run} disabled={aiLoading} data-testid="s3-run-ai" className="rounded-full">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Analizza rischi territoriali
      </Button>
      {data && Object.keys(data).length > 0 && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          {Object.entries(data).filter(([k]) => k !== "_input_descr").map(([k, v]) => (
            <div key={k} className="border border-border rounded-md p-3 bg-muted/30">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{k}</div>
              <div className="text-sm mt-1">{typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepNioshTabs({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const [mode, setMode] = useState("multitask");
  const mansioniOptions = (dvr?.steps?.step4_mansioni || []).map((m) => m.nome || m.mansione).filter(Boolean);
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setMode("multitask")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${mode === "multitask" ? "border-slate-900 text-slate-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="niosh-tab-multitask"
        >
          Task multitask (CLI)
        </button>
        <button
          onClick={() => setMode("legacy")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${mode === "legacy" ? "border-slate-900 text-slate-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="niosh-tab-legacy"
        >
          Quick analysis (singolo video)
        </button>
      </div>
      {mode === "multitask" ? (
        <StepNioshMultitask data={data} onChange={onChange} aiLoading={aiLoading} setAiLoading={setAiLoading} mansioniOptions={mansioniOptions} />
      ) : (
        <StepNiosh data={data} onChange={onChange} aiLoading={aiLoading} setAiLoading={setAiLoading} />
      )}
    </div>
  );
}

function StepOcraTabs({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const [mode, setMode] = useState("multitask");
  const mansioniOptions = (dvr?.steps?.step4_mansioni || []).map((m) => m.nome || m.mansione).filter(Boolean);
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setMode("multitask")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${mode === "multitask" ? "border-slate-900 text-slate-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="ocra-tab-multitask"
        >
          Task multitask
        </button>
        <button
          onClick={() => setMode("legacy")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${mode === "legacy" ? "border-slate-900 text-slate-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="ocra-tab-legacy"
        >
          Quick analysis (singolo video)
        </button>
      </div>
      {mode === "multitask" ? (
        <StepOcraMultitask data={data} onChange={onChange} aiLoading={aiLoading} setAiLoading={setAiLoading} mansioniOptions={mansioniOptions} />
      ) : (
        <StepOcra data={data} onChange={onChange} aiLoading={aiLoading} setAiLoading={setAiLoading} />
      )}
    </div>
  );
}

function StepNiosh({ data, onChange, aiLoading, setAiLoading }) {
  const list = Array.isArray(data) ? data : [];
  const fileRef = useRef(null);
  const [pending, setPending] = useState({ video: null, peso: 10, forza: 0 });
  const analyze = async () => {
    if (!pending.video) return toast.error("Carica un video");
    setAiLoading(true);
    const fd = new FormData();
    fd.append("file", pending.video);
    fd.append("peso_kg", String(pending.peso));
    fd.append("forza_extra", String(pending.forza));
    try {
      const r = await api.post("/dvr/ai/niosh", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      onChange([...list, { ...r.data, _uid: uid(), mansione: pending.mansione || "" }]);
      setPending({ video: null, peso: 10, forza: 0 });
    } catch (err) { toast.error(err.response?.data?.detail || "AI error"); }
    setAiLoading(false);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Carica un video di sollevamento carichi. L'AI estrae frame e calcola l'indice NIOSH (LI). Inserisci peso e forza aggiuntiva (necessari perché non estraibili dal video).</p>
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-border rounded-md p-4 text-sm hover:bg-muted/30 col-span-3" data-testid="niosh-upload">
          <Video className="w-5 h-5 mx-auto mb-1" />
          {pending.video ? pending.video.name : "Video sollevamento"}
        </button>
        <input ref={fileRef} type="file" accept="video/*" capture="environment" hidden onChange={(e) => setPending({ ...pending, video: e.target.files?.[0] || null })} />
        <div><Label>Peso carico (kg)</Label><Input type="number" step="0.1" value={pending.peso} onChange={(e) => setPending({ ...pending, peso: Number(e.target.value) })} data-testid="niosh-peso" /></div>
        <div><Label>Forza extra (kg)</Label><Input type="number" step="0.1" value={pending.forza} onChange={(e) => setPending({ ...pending, forza: Number(e.target.value) })} data-testid="niosh-forza" /></div>
        <Button onClick={analyze} disabled={aiLoading || !pending.video} className="rounded-full self-end" data-testid="niosh-analyze">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Analizza
        </Button>
      </div>
      <div className="space-y-3">
        {list.map((n, i) => (
          <div key={n._uid || `niosh-${i}`} className="border border-border rounded-md p-3">
            <div className="flex justify-between">
              <div className="font-semibold">LI = {n.LI ?? "?"} · <span className={`text-xs px-2 py-0.5 rounded ${n.classificazione === "accettabile" ? "bg-emerald-100 text-emerald-700" : n.classificazione === "attenzione" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{n.classificazione}</span></div>
              <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-xs mt-2 text-muted-foreground">RWL {n.RWL_kg} kg · Peso {n.peso_kg} kg</div>
            <div className="text-xs mt-1">{n.commento}</div>
            {n.frames?.length > 0 && (
              <div className="flex gap-2 mt-2">
                {n.frames.map((u) => <img key={u} src={`${BACKEND}${u}`} alt="" className="w-20 h-14 object-cover rounded border" />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepOcra({ data, onChange, aiLoading, setAiLoading }) {
  const list = Array.isArray(data) ? data : [];
  const fileRef = useRef(null);
  const [pending, setPending] = useState({ video: null, forza: 3, turno: 480 });
  const analyze = async () => {
    if (!pending.video) return toast.error("Carica un video");
    setAiLoading(true);
    const fd = new FormData();
    fd.append("file", pending.video);
    fd.append("forza_media", String(pending.forza));
    fd.append("durata_turno_min", String(pending.turno));
    try {
      const r = await api.post("/dvr/ai/ocra", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      onChange([...list, { ...r.data, _uid: uid() }]);
      setPending({ video: null, forza: 3, turno: 480 });
    } catch (err) { toast.error(err.response?.data?.detail || "AI error"); }
    setAiLoading(false);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Carica un video di lavorazioni ripetitive arti superiori. L'AI valuta cicli, recuperi e posture. Fornisci forza media (Borg CR-10) e durata turno.</p>
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-border rounded-md p-4 text-sm hover:bg-muted/30 col-span-3" data-testid="ocra-upload">
          <Video className="w-5 h-5 mx-auto mb-1" />
          {pending.video ? pending.video.name : "Video ciclo lavorativo"}
        </button>
        <input ref={fileRef} type="file" accept="video/*" capture="environment" hidden onChange={(e) => setPending({ ...pending, video: e.target.files?.[0] || null })} />
        <div><Label>Forza (Borg 0-10)</Label><Input type="number" step="0.5" value={pending.forza} onChange={(e) => setPending({ ...pending, forza: Number(e.target.value) })} data-testid="ocra-forza" /></div>
        <div><Label>Durata turno (min)</Label><Input type="number" value={pending.turno} onChange={(e) => setPending({ ...pending, turno: Number(e.target.value) })} data-testid="ocra-turno" /></div>
        <Button onClick={analyze} disabled={aiLoading || !pending.video} className="rounded-full self-end" data-testid="ocra-analyze">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Analizza
        </Button>
      </div>
      <div className="space-y-3">
        {list.map((o, i) => (
          <div key={o._uid || `ocra-${i}`} className="border border-border rounded-md p-3">
            <div className="flex justify-between">
              <div className="font-semibold">OCRA index = {o.OCRA_index ?? "?"} · <span className="text-xs px-2 py-0.5 rounded bg-muted">{o.fascia}</span></div>
              <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-xs mt-1">{o.commento}</div>
            {o.frames?.length > 0 && (
              <div className="flex gap-2 mt-2">
                {o.frames.map((u) => <img key={u} src={`${BACKEND}${u}`} alt="" className="w-20 h-14 object-cover rounded border" />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const SIGN_ROLES = [
  { key: "datore_di_lavoro", label: "Datore di lavoro" },
  { key: "rspp", label: "RSPP" },
  { key: "medico", label: "Medico competente" },
  { key: "rls", label: "RLS" },
  { key: "rlst", label: "RLST" },
  { key: "consulente", label: "Consulente" },
];

function StepFirme({ dvr, onSigned }) {
  const firme = dvr.steps?.firme || {};
  const [activeRole, setActiveRole] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [uploadedFilename, setUploadedFilename] = useState("");
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const uploadRef = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#111"; ctx.lineWidth = 2; ctx.lineJoin = "round"; ctx.lineCap = "round";
    setUploadedFilename("");
  }, [activeRole]);

  const start = (e) => { drawingRef.current = true; move(e, true); };
  const move = (e, first) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    const x = p.clientX - rect.left; const y = p.clientY - rect.top;
    const ctx = c.getContext("2d");
    if (first) { ctx.beginPath(); ctx.moveTo(x, y); } else { ctx.lineTo(x, y); ctx.stroke(); }
  };
  const stop = () => { drawingRef.current = false; };
  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    setUploadedFilename("");
  };

  const uploadImage = (file) => {
    if (!file) return;
    const c = canvasRef.current;
    if (!c) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ctx = c.getContext("2d");
        // Sfondo bianco
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, c.width, c.height);
        // Fit dentro il canvas mantenendo aspect ratio
        const scale = Math.min(c.width / img.width, c.height / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const dx = (c.width - w) / 2;
        const dy = (c.height - h) / 2;
        try {
          ctx.drawImage(img, dx, dy, w, h);
          setUploadedFilename(file.name);
          toast.success("Immagine caricata — controlla l'anteprima e salva");
        } catch (err) {
          toast.error("Impossibile disegnare l'immagine");
        }
      };
      img.onerror = () => toast.error("File immagine non valido");
      img.src = reader.result;
    };
    reader.onerror = () => toast.error("Errore lettura file");
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!activeRole) return;
    if (!signerName.trim()) return toast.error("Inserisci il nome del firmatario");
    const c = canvasRef.current; if (!c) return;
    const dataUrl = c.toDataURL("image/png");
    try {
      await api.post(`/dvr/documents/${dvr.id}/sign`, { role: activeRole, name: signerName, image_base64: dataUrl });
      toast.success("Firma salvata");
      setActiveRole(null); setSignerName(""); setUploadedFilename(""); onSigned();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore firma"); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Raccogli le firme grafiche di ciascun ruolo. Puoi disegnarle a mano o <b>caricare un'immagine</b> (es. scansione firma su carta, PNG/JPG). Vengono incluse automaticamente nel DVR generato.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {SIGN_ROLES.map((r) => (
          <div key={r.key} className="border border-border rounded-md p-3">
            <div className="text-sm font-semibold">{r.label}</div>
            {firme[r.key] ? (
              <>
                <div className="text-xs text-muted-foreground">{firme[r.key].name}</div>
                <div className="text-[10px] text-muted-foreground">{(firme[r.key].signed_at || "").slice(0, 10)}</div>
                <img src={`${BACKEND}${firme[r.key].url}`} alt="firma" className="mt-2 h-16 object-contain" />
                <Button size="sm" variant="ghost" className="mt-2 rounded-full text-xs" onClick={() => setActiveRole(r.key)} data-testid={`sign-replace-${r.key}`}>
                  <PenLine className="w-3 h-3 mr-1" /> Sostituisci
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" className="mt-2 rounded-full" onClick={() => setActiveRole(r.key)} data-testid={`sign-btn-${r.key}`}>
                <PenLine className="w-3.5 h-3.5 mr-1" /> Firma
              </Button>
            )}
          </div>
        ))}
      </div>

      {activeRole && (
        <div className="border border-border rounded-md p-4 bg-muted/20 space-y-3">
          <div className="flex items-center gap-3">
            <div className="font-semibold">Firma: {SIGN_ROLES.find((x) => x.key === activeRole)?.label}</div>
            <button onClick={() => { setActiveRole(null); setUploadedFilename(""); }} className="text-xs text-muted-foreground hover:text-foreground ml-auto">Annulla</button>
          </div>
          <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Nome e cognome del firmatario" data-testid="sign-name" />
          <canvas
            ref={canvasRef}
            width={600} height={180}
            className="border border-border bg-white rounded-md w-full touch-none"
            data-testid="sign-canvas"
            onMouseDown={start} onMouseMove={(e) => move(e, false)} onMouseUp={stop} onMouseLeave={stop}
            onTouchStart={start} onTouchMove={(e) => { e.preventDefault(); move(e, false); }} onTouchEnd={stop}
          />
          {uploadedFilename && (
            <div className="text-[11px] text-muted-foreground italic">Immagine caricata: {uploadedFilename}</div>
          )}
          <input
            ref={uploadRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            hidden
            data-testid="sign-upload-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadImage(f);
              e.target.value = "";
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => uploadRef.current?.click()} className="rounded-full" data-testid="sign-upload-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> Carica immagine
            </Button>
            <Button variant="outline" size="sm" onClick={clear} className="rounded-full">Cancella</Button>
            <Button size="sm" onClick={save} className="rounded-full" data-testid="sign-save">Salva firma</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function StepVDT({ data, onChange, aiLoading, setAiLoading }) {
  const list = Array.isArray(data) ? data : [];
  const [pending, setPending] = useState({ postazione: null, monitor: null, seduta: null, lux: 400, tempo: 6 });
  const refs = { postazione: useRef(null), monitor: useRef(null), seduta: useRef(null) };
  const analyze = async () => {
    if (!pending.postazione && !pending.monitor && !pending.seduta) return toast.error("Carica almeno una foto");
    setAiLoading(true);
    const fd = new FormData();
    fd.append("lux", String(pending.lux));
    fd.append("tempo_utilizzo_h", String(pending.tempo));
    if (pending.postazione) fd.append("file_postazione", pending.postazione);
    if (pending.monitor) fd.append("file_monitor", pending.monitor);
    if (pending.seduta) fd.append("file_seduta", pending.seduta);
    try {
      const r = await api.post("/dvr/ai/vdt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      onChange([...list, { ...r.data, _uid: uid() }]);
      setPending({ postazione: null, monitor: null, seduta: null, lux: 400, tempo: 6 });
    } catch (err) { toast.error(err.response?.data?.detail || "AI error"); }
    setAiLoading(false);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const uploadTile = (kind, label) => (
    <>
      <button onClick={() => refs[kind].current?.click()} className="border-2 border-dashed border-border rounded-md p-3 text-xs hover:bg-muted/30" data-testid={`vdt-upload-${kind}`}>
        <Camera className="w-4 h-4 mx-auto mb-1" />
        {pending[kind] ? pending[kind].name.slice(0, 20) : label}
      </button>
      <input ref={refs[kind]} type="file" accept="image/*" capture="environment" hidden onChange={(e) => setPending({ ...pending, [kind]: e.target.files?.[0] || null })} />
    </>
  );
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Fotografa la postazione VDT (Titolo VII D.Lgs. 81/08 + ISO 9241). Inserisci lux e tempo utilizzo (h/giorno).</p>
      <div className="grid grid-cols-3 gap-2">
        {uploadTile("postazione", "Postazione panoramica")}
        {uploadTile("monitor", "Monitor")}
        {uploadTile("seduta", "Seduta")}
        <div><Label>Lux</Label><Input type="number" value={pending.lux} onChange={(e) => setPending({ ...pending, lux: Number(e.target.value) })} data-testid="vdt-lux" /></div>
        <div><Label>Tempo utilizzo (h/giorno)</Label><Input type="number" step="0.5" value={pending.tempo} onChange={(e) => setPending({ ...pending, tempo: Number(e.target.value) })} data-testid="vdt-tempo" /></div>
        <Button onClick={analyze} disabled={aiLoading} className="rounded-full self-end" data-testid="vdt-analyze">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Analizza
        </Button>
      </div>
      <div className="space-y-3">
        {list.map((v, i) => (
          <div key={v._uid || `vdt-${i}`} className="border border-border rounded-md p-3">
            <div className="flex justify-between items-start">
              <div className="font-semibold flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                Postazione VDT #{i + 1}
                <span className={`text-xs px-2 py-0.5 rounded ${v.conformita_globale === "conforme" ? "bg-emerald-100 text-emerald-700" : v.conformita_globale === "parzialmente" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{v.conformita_globale}</span>
              </div>
              <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-xs mt-2 text-muted-foreground">{v.lux} lux · {v.tempo_utilizzo_h} h/gg · Rischio residuo: {v.rischio_residuo}</div>
            {["monitor", "seduta", "postura", "illuminazione"].map((k) => v[k] && (
              <div key={k} className="text-xs mt-1"><b>{k.charAt(0).toUpperCase() + k.slice(1)}:</b> {v[k].conforme ? "✓" : "✗"} {v[k].note}</div>
            ))}
            {v.sorveglianza_sanitaria_obbligo && <div className="text-xs mt-1 text-rose-700 font-semibold">Sorveglianza sanitaria obbligatoria (art. 176)</div>}
            {v.photos?.length > 0 && (
              <div className="flex gap-2 mt-2">
                {v.photos.map((u) => <img key={u} src={`${BACKEND}${u}`} alt="" className="w-24 h-16 object-cover rounded border" />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function StepIncendio({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  // Merge existing questionnaire values or seed from step1
  const q = d.questionario || {
    tipo_attivita: s1.tipo_attivita || "",
    ateco: s1.ateco || "",
    superficie_mq: "",
    numero_occupanti: s1.numero_lavoratori || "",
    piani: "",
    piano_seminterrato: false,
    has_depositi_infiammabili: false,
    quantita_infiammabili_kg: "",
    has_centrali_termiche: false,
    potenza_termica_kw: "",
    has_autorimesse: false,
    autorimessa_posti: "",
    has_gpl: false,
    impianti_elettrici_speciali: false,
    carico_incendio_stimato_alto: false,
    vie_uscita_esistenti: "",
    larghezza_uscite_cm: "",
    presenza_estintori: false,
    presenza_idranti: false,
    presenza_rilevazione_fumi: false,
    presenza_illuminazione_emergenza: false,
    compartimentazione_esistente: false,
    prossimita_attivita_pericolose: false,
    note: "",
  };
  const setQ = (k, v) => onChange({ ...d, questionario: { ...q, [k]: v } });

  const run = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/incendio", q, { timeout: 120000 });
      onChange({ ...d, questionario: q, ...r.data });
      toast.success("Valutazione incendio completata");
    } catch (err) { toast.error(err.response?.data?.detail || "AI error"); }
    setAiLoading(false);
  };

  const B = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={!!q[k]} onChange={(e) => setQ(k, e.target.checked)} data-testid={`inc-${k}`} />
      {label}
    </label>
  );
  const N = ({ k, label, ph }) => (
    <div><Label>{label}</Label><Input value={q[k] ?? ""} onChange={(e) => setQ(k, e.target.value)} placeholder={ph} data-testid={`inc-${k}`} /></div>
  );

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Questionario intelligente ai sensi dei DM 1/2/3 settembre 2021. L'AI determinerà livello rischio, misure, esodo, formazione addetti, obblighi CPI.</p>

      <section className="grid grid-cols-3 gap-3">
        <N k="tipo_attivita" label="Tipo attività" ph="es. ufficio, deposito, officina" />
        <N k="ateco" label="ATECO" />
        <N k="superficie_mq" label="Superficie totale (mq)" />
        <N k="numero_occupanti" label="Numero occupanti max" />
        <N k="piani" label="Numero piani" />
        <div className="col-span-3 grid grid-cols-3 gap-2 text-sm bg-muted/30 rounded p-3">
          <B k="piano_seminterrato" label="Piano seminterrato/interrato" />
          <B k="has_depositi_infiammabili" label="Depositi infiammabili" />
          <B k="has_centrali_termiche" label="Centrali termiche" />
          <B k="has_autorimesse" label="Autorimesse" />
          <B k="has_gpl" label="Serbatoi GPL/metano" />
          <B k="impianti_elettrici_speciali" label="Impianti elettrici speciali" />
          <B k="carico_incendio_stimato_alto" label="Carico incendio alto (>450 MJ/m²)" />
          <B k="prossimita_attivita_pericolose" label="Attività pericolose vicine" />
        </div>
        {q.has_depositi_infiammabili && <N k="quantita_infiammabili_kg" label="Quantità infiammabili (kg)" />}
        {q.has_centrali_termiche && <N k="potenza_termica_kw" label="Potenza termica (kW)" />}
        {q.has_autorimesse && <N k="autorimessa_posti" label="Autorimessa: n. posti auto" />}
      </section>

      <section className="border-t border-border pt-3 grid grid-cols-3 gap-3">
        <h3 className="col-span-3 font-semibold text-sm">Vie di esodo & impianti antincendio esistenti</h3>
        <N k="vie_uscita_esistenti" label="N° vie di uscita" />
        <N k="larghezza_uscite_cm" label="Larghezza uscite (cm)" />
        <div className="col-span-3 grid grid-cols-3 gap-2 text-sm bg-muted/30 rounded p-3">
          <B k="presenza_estintori" label="Estintori portatili" />
          <B k="presenza_idranti" label="Idranti UNI 45/70" />
          <B k="presenza_rilevazione_fumi" label="Rilevazione fumi" />
          <B k="presenza_illuminazione_emergenza" label="Illuminazione emergenza" />
          <B k="compartimentazione_esistente" label="Compartimentazione REI" />
        </div>
      </section>

      <Textarea rows={2} placeholder="Note libere (destinazione d'uso specifica, contesto, ...)" value={q.note || ""} onChange={(e) => setQ("note", e.target.value)} />

      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="inc-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Valuta con AI
      </Button>

      {d.livello_rischio && (
        <div className="space-y-3 border-t border-border pt-4">
          <div className={`rounded-md p-4 ${d.livello_rischio.valore === "basso" ? "bg-emerald-50 border border-emerald-300" : d.livello_rischio.valore === "medio" ? "bg-amber-50 border border-amber-300" : "bg-rose-50 border border-rose-300"}`}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Livello di rischio incendio</div>
            <div className="text-2xl font-bold mt-1">{d.livello_rischio.valore?.toUpperCase()}</div>
            <div className="text-sm mt-1">{d.livello_rischio.motivazione}</div>
            <div className="text-xs text-muted-foreground mt-1">Rif: {d.livello_rischio.normativa}</div>
          </div>

          {d.attivita_dpr151?.soggetta && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-3">
              <div className="font-semibold text-sm">Attività soggetta a CPI (categoria {d.attivita_dpr151.categoria})</div>
              <div className="text-xs">{(d.attivita_dpr151.attivita_ricadenti || []).join(", ")}</div>
            </div>
          )}

          {d.protezione_attiva && (
            <details className="border border-border rounded-md p-3" open>
              <summary className="font-semibold cursor-pointer">Protezione attiva</summary>
              <div className="text-sm mt-2 space-y-1">
                {Object.entries(d.protezione_attiva).map(([k, v]) => (
                  <div key={k}><b>{k.replace(/_/g, " ")}:</b> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                ))}
              </div>
            </details>
          )}

          {d.esodo && (
            <details className="border border-border rounded-md p-3">
              <summary className="font-semibold cursor-pointer">Esodo</summary>
              <div className="text-sm mt-2 space-y-1">
                {Object.entries(d.esodo).map(([k, v]) => (
                  <div key={k}><b>{k.replace(/_/g, " ")}:</b> {Array.isArray(v) ? v.join(", ") : String(v)}</div>
                ))}
              </div>
            </details>
          )}

          {d.formazione_addetti && (
            <div className="border border-border rounded-md p-3">
              <div className="font-semibold text-sm">Formazione addetti antincendio</div>
              <div className="text-sm mt-1">
                Livello <b>{d.formazione_addetti.livello}</b> · {d.formazione_addetti.ore_iniziali} ore iniziali · Aggiornamento {d.formazione_addetti.aggiornamento_periodicita}
                {d.formazione_addetti.esame_prevenzione_incendi_obbligo && <span className="ml-2 text-rose-700 font-semibold">Esame VVF obbligatorio</span>}
              </div>
            </div>
          )}

          {d.azioni_prioritarie?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm">Azioni prioritarie</h4>
              <ul className="mt-1 space-y-1">
                {d.azioni_prioritarie.map((a, i) => (
                  <li key={`${a.priorita || "p"}-${a.azione || i}`} className="text-sm flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${a.priorita === "urgente" ? "bg-rose-100 text-rose-700" : a.priorita === "alta" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>{(a.priorita || "").toUpperCase()}</span>
                    <span>{a.azione}</span>
                    <span className="text-xs text-muted-foreground">{a.tempistica}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
        </div>
      )}
    </div>
  );
}


function StepSorveglianza({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const s1 = dvr.steps?.step1_dati_generali || {};
  const s4 = dvr.steps?.step4_mansioni || [];
  const [manualMansione, setManualMansione] = useState("");
  const d = data || {};
  const protocolli = d.protocollo || [];

  const buildPayload = () => {
    const mansioni = (s4 || []).map((m) => ({
      nome: m.mansione,
      rischi: (m.rischi || []).map((r) => r.nome).filter(Boolean),
      dpi: (m.dpi_proposti || []).map((x) => x.tipo).filter(Boolean),
      attrezzature: m.attrezzature_tipiche || [],
      sostanze: m.sostanze_tipiche || [],
    }));
    if (manualMansione.trim()) {
      mansioni.push({ nome: manualMansione.trim(), rischi: [] });
    }
    return { ateco: s1.ateco, tipo_attivita: s1.tipo_attivita, mansioni };
  };

  const run = async () => {
    const payload = buildPayload();
    if (!payload.mansioni.length) return toast.error("Inserisci almeno una mansione (step 4) o digitane una manuale");
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/sorveglianza", payload, { timeout: 120000 });
      // Se questa è la prima elaborazione, sostituisce; se stiamo aggiungendo una manuale, appende.
      const existing = d.protocollo || [];
      const isAdditive = existing.length > 0 && manualMansione.trim();
      if (isAdditive) {
        onChange({
          ...d,
          protocollo: [...existing, ...(r.data.protocollo || [])],
          medico_competente_obbligo: d.medico_competente_obbligo || r.data.medico_competente_obbligo,
          sintesi: r.data.sintesi || d.sintesi,
        });
      } else {
        onChange(r.data);
      }
      setManualMansione("");
      toast.success(isAdditive ? "Protocollo aggiunto" : "Protocollo sanitario generato");
    } catch (err) { toast.error(err.response?.data?.detail || "AI error"); }
    setAiLoading(false);
  };

  const removeProtocol = (i) => {
    const next = { ...d, protocollo: (d.protocollo || []).filter((_, idx) => idx !== i) };
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Genera il protocollo di sorveglianza sanitaria (D.Lgs. 81/08 art. 41) per ogni mansione presente nello Step 4. Puoi aggiungerne una manuale.</p>

      <div className="border border-border rounded-md p-3 bg-muted/20">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Mansioni in valutazione</div>
        <div className="flex flex-wrap gap-1.5">
          {s4.length === 0 && <span className="text-sm text-muted-foreground italic">Nessuna mansione in step 4 — usa il campo manuale sotto o compila prima lo step 4.</span>}
          {s4.map((m, i) => (
            <span key={`${m.mansione || "m"}-${i}`} className="text-xs bg-card border border-border px-2 py-1 rounded-full">{m.mansione}</span>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Input value={manualMansione} onChange={(e) => setManualMansione(e.target.value)} placeholder="Aggiungi mansione manuale (opz.) es. 'Autista camion'" data-testid="sorv-manual" />
        <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="sorv-run-ai">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-2" />} Elabora protocollo
        </Button>
      </div>

      {d.medico_competente_obbligo !== undefined && (
        <div className={`rounded-md p-3 text-sm ${d.medico_competente_obbligo ? "bg-amber-50 border border-amber-300" : "bg-emerald-50 border border-emerald-300"}`}>
          <b>Medico Competente:</b> {d.medico_competente_obbligo ? "nomina OBBLIGATORIA (D.Lgs. 81/08 art. 18 c.1 lett. a)" : "non richiesto"}
        </div>
      )}

      <div className="space-y-3">
        {protocolli.map((m, i) => (
          <div key={`${m.mansione || "prot"}-${i}`} className="border border-border rounded-md p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold flex items-center gap-2"><Stethoscope className="w-4 h-4" /> {m.mansione}</h3>
                <div className="text-xs text-muted-foreground mt-0.5">Rischi: {(m.rischi_considerati || []).join(", ") || "—"}</div>
              </div>
              <button onClick={() => removeProtocol(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Obbligo</div>
                <div className="mt-0.5">
                  <span className={`text-xs px-2 py-0.5 rounded ${m.obbligo?.valore ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {m.obbligo?.valore ? "SÌ" : "NO"}
                  </span>
                  <div className="text-xs mt-1">{m.obbligo?.motivazione}</div>
                  <div className="text-[11px] text-muted-foreground">Rif: {m.obbligo?.normativa}</div>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Periodicità</div>
                <div className="mt-0.5 font-semibold">{m.periodicita_visite?.valore || "—"}</div>
                <div className="text-[11px] text-muted-foreground">Rif: {m.periodicita_visite?.normativa}</div>
              </div>
            </div>
            {m.esami_richiesti?.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Esami richiesti</div>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {m.esami_richiesti.map((e, ii) => (
                    <li key={`${e.esame || "es"}-${ii}`}>• {e.esame}{e.note ? ` — ${e.note}` : ""} <span className="text-[11px] text-muted-foreground">({e.normativa})</span></li>
                  ))}
                </ul>
              </div>
            )}
            {m.vaccinazioni?.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Syringe className="w-3 h-3" /> Vaccinazioni</div>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {m.vaccinazioni.map((v, ii) => (
                    <li key={`${v.vaccino || "vac"}-${ii}`}>
                      • {v.vaccino}{" "}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${v.raccomandato_obbligatorio === "obb" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>
                        {v.raccomandato_obbligatorio === "obb" ? "OBBLIGATORIO" : "raccomandato"}
                      </span>
                      <span className="text-[11px] text-muted-foreground ml-1">({v.normativa})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {m.protocollo_specifico && (
              <div className="mt-3 text-sm">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Protocollo specifico</div>
                <div>{m.protocollo_specifico}</div>
              </div>
            )}
            {m.documenti_prodotti?.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">Documenti prodotti: {m.documenti_prodotti.join(" · ")}</div>
            )}
          </div>
        ))}
      </div>

      {d.sintesi && (
        <div className="text-sm text-muted-foreground italic border-t border-border pt-3">{d.sintesi}</div>
      )}
    </div>
  );
}

